import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBillingContext } from '~/contexts/BillingContext'
import { SidebarProvider } from '~/contexts/SidebarContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import Sidebar from '../sidebar/Sidebar'

/**
 * Component displayed when user has no workspaces available.
 * Shows a minimal sidebar (without workspace switching) and allows
 * billing admins to create a new workspace.
 */
export function NoWorkspaceView() {
  const navigate = useNavigate()
  const { isBillingAdmin } = useBillingContext()
  const { createNewWorkspace } = useWorkspaceContext()
  const [newWorkspaceName, setNewWorkspaceName] = useState('')
  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false)

  const handleCreateWorkspace = async () => {
    if (!newWorkspaceName.trim() || isCreatingWorkspace) return
    setIsCreatingWorkspace(true)
    try {
      await createNewWorkspace(newWorkspaceName.trim())
      // createNewWorkspace handles navigation to the new workspace
    } catch (error) {
      console.error('Failed to create workspace:', error)
      setIsCreatingWorkspace(false)
    }
  }

  return (
    <SidebarProvider>
      <div className='h-screen w-screen flex bg-light-background dark:bg-dark-background'>
        {/* Sidebar in minimal mode - no workspace switching or conversations */}
        <Sidebar handleRoot={() => navigate('/')} minimalMode />
        {/* Main content */}
        <div className='flex-1 flex items-center justify-center'>
          <div className='text-center max-w-md p-8'>
            <h1 className='text-2xl font-bold mb-4 text-gray-900 dark:text-gray-100'>
              No Workspace Available
            </h1>
            <p className='text-gray-600 dark:text-gray-400 mb-4'>
              You don&apos;t have access to any workspaces.
            </p>
            {isBillingAdmin ? (
              <>
                <p className='text-sm text-gray-500 dark:text-gray-500 mb-6'>
                  As an account admin, you can create a new workspace to get started.
                </p>
                <div className='flex flex-col gap-3'>
                  <input
                    type='text'
                    placeholder='Enter workspace name'
                    value={newWorkspaceName}
                    onChange={e => setNewWorkspaceName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        void handleCreateWorkspace()
                      }
                    }}
                    className='px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                    disabled={isCreatingWorkspace}
                  />
                  <button
                    onClick={() => void handleCreateWorkspace()}
                    disabled={!newWorkspaceName.trim() || isCreatingWorkspace}
                    className='bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white font-medium py-2 px-6 rounded-lg transition-colors'
                  >
                    {isCreatingWorkspace ? 'Creating...' : 'Create Workspace'}
                  </button>
                </div>
              </>
            ) : (
              <p className='text-sm text-gray-500 dark:text-gray-500'>
                Please contact your administrator to get access.
              </p>
            )}
          </div>
        </div>
      </div>
    </SidebarProvider>
  )
}

export default NoWorkspaceView
